<template>
  <div>
    <ArticleInfo :article="article" />
    <hr />

    <MessageBlock v-if="saveError" heading="Failed to save article" class="is-danger">
      <p>{{ saveError }}</p>
    </MessageBlock>

    <VForm v-slot="{ setFieldError, validateField }">
      <form @submit.prevent>
  
      
        <fieldset :disabled="isFormLocked">

          <!-- ROW 1 (Add related article) -->
          <div class="columns is-mobile">
            <div class="column is-two-fifths">
              <TextInput
                customKeydownEnter
                rules="articleId"
                id="searchArticleId"
                label="Add related article"
                placeholder="e.g. A23744"
                @keydown-enter="getArticleById(setFieldError)"
                v-model.trim="searchArticleId">
                <template #addons>
                  <div class="control">
                    <button type="button" class="button is-link" @click="getArticleById(setFieldError, validateField)">Add</button>
                  </div>
                </template>
              </TextInput>
            </div>
          </div>
          <!-- ROW 2 (Related articles) -->
          <ArticleCard
            v-for="articleId in article.relatedArticles"
            :key="articleId"
            :articleId="articleId"
            :showButtons="true"
            @remove="removeRelatedArticle(articleId)"
          />

          <!-- ROW 3 (Note) -->
          <div class="field">
            <label class="label">Note</label>
            <div class="control">
              <HtmlEditor 
                id="related-articles-note"
                :disabled="isFormLocked"
                :init="TinyMceConfig" 
                v-model="note" />
            </div>
          </div>

          <NextPreviousButtons v-if="!isArticleReadOnly">
            <template #prev>
              <button
                type="button"
                class="button"
                @click="saveArticleAndContinue('Compounds')"
              >
                Save and go back
              </button>
            </template>
            <template #next>
              <button
                type="button"
                class="button is-success"
                @click="saveArticleAndContinue('Preview')"
              >
                Save and continue
              </button>
            </template>
          </NextPreviousButtons>

        </fieldset>
      </form>
    </VForm>
  </div>
</template>

<script>
import { Form as VForm } from 'vee-validate';
import { MarinLitDataService } from '@/api';
import ArticleTabsMixin from '@/mixins/marinlit/article-tabs-mixin';
import { useMarinLitEditorStore } from '@/store/modules/marinlit/editor';

export default {
  name: 'TabRelated',
  mixins: [ArticleTabsMixin],
  components: {
    VForm,
  },
  setup() {
    const marinLitEditorStore = useMarinLitEditorStore();

    return {
      marinLitEditorStore,
    };
  },
  props: {
    saveArticle: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      searchArticleId: '',
    }
  },
  computed: {
    relatedArticles: {
      get() {
        return this.marinLitEditorStore.article.relatedArticles;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.relatedArticles',
          value,
        });
      }
    },
    note: {
      get() {
        return this.marinLitEditorStore.article.note;
      },
      set(value) {
        this.marinLitEditorStore.updateField({
          path: 'article.note',
          value,
        });
      }
    }
  },
  methods: {
    addMultiRowValueAction({ key, value }) {
      this.marinLitEditorStore.addMultiRowValueAction({ key, value });
    },
    removeValueFromArrayAction({ key, value }) {
      this.marinLitEditorStore.removeValueFromArrayAction({ key, value });
    },
    addRelatedArticle(articleId) {
      this.addMultiRowValueAction({ key: 'relatedArticles', value: articleId });
    },
    removeRelatedArticle(articleId) {
      this.removeValueFromArrayAction({ key: 'relatedArticles', value: articleId });
    },
    async getArticleById(setFieldError, validateField) {
      if (!this.searchArticleId) return;

      // validates the rule 'articleId'
      const resultValidation = await validateField?.('searchArticleId');
      if (resultValidation?.valid === false) return;


      // Find the requested article using the search results API
      const result = await MarinLitDataService.getSearchResults(`marinLitId=${this.searchArticleId}`);

      if (result.Data.searchResults) {
        // Check that it's not a duplicate etc.
        const articleId = this.validateRelatedArticle(result.Data.searchResults, setFieldError);
        if (articleId) {
          this.addRelatedArticle(articleId);
          this.searchArticleId = '';
        }
      } else { // API error
        setFieldError('searchArticleId', result.ServiceStatus.Error);
      }
    },
    validateRelatedArticle(results, setFieldError) {
      let error = '';
      if (!results.length) {
        error = `${this.searchArticleId} doesn't exist or was discarded`;
      } else {
        const article = results[0];
        if (article.articleId === this.article.articleId) {
          error = `${article.marinLitId} can't be added to itself`;
        } else if (this.relatedArticles.includes(article.articleId)) {
          error = `${article.marinLitId} was already added to this article`;
        } else {         // Passed validation, so return the ID of the related article
          return article.articleId; // Valid
        }
      }

      setFieldError('searchArticleId', error);
      return null;
    },
    async saveArticleAndContinue(nextTab) {
      if (!this.saveArticle) return;

      try {
        this.$data.isFormSaving = true;
        await this.saveArticle(nextTab);
        if (nextTab) this.switchTab(nextTab);
      } finally {
        this.$data.isFormSaving = false;
      }
    }
  }
}
</script>